import { ref, watch } from "vue";
import { usePreset } from "@primeuix/themes";
import { persologDefault, persologHighContrast } from "@persolog/theme";
import { applyThemeAttribute, normalizeTheme } from "@persolog/theme/themes";

// Presets re-exported so existing imports keep working (mirrors the other apps).
export { persologDefault, persologHighContrast };

export type ThemeType = "default" | "high-contrast";

const STORAGE_KEY = "selectedTheme";

function readStoredTheme(): ThemeType {
    return localStorage.getItem(STORAGE_KEY) === "high-contrast" ? "high-contrast" : "default";
}

// admin has no pinia store, so the theme is a module-level ref persisted to
// localStorage directly (matching admin's localStorage-based preferences). The
// other apps route this through their preferences store instead.
const currentTheme = ref<ThemeType>(readStoredTheme());

export function useTheme() {
    const applyTheme = (newTheme: ThemeType) => {
        // Document-root class consumed by high-contrast-theme.css
        document.documentElement.classList.remove("theme-default", "theme-high-contrast");
        document.documentElement.classList.add(`theme-${newTheme}`);
        // Drives the Tailwind token layer in @persolog/theme/tokens.css.
        applyThemeAttribute(normalizeTheme(newTheme));

        // Swap PrimeVue's active preset
        usePreset(newTheme === "high-contrast" ? persologHighContrast : persologDefault);
    };

    const setTheme = (newTheme: ThemeType) => {
        localStorage.setItem(STORAGE_KEY, newTheme);
        currentTheme.value = newTheme;
    };

    const toggleTheme = () => {
        setTheme(currentTheme.value === "default" ? "high-contrast" : "default");
    };

    watch(currentTheme, (newTheme) => applyTheme(newTheme), { immediate: true });

    return {
        theme: currentTheme,
        setTheme,
        toggleTheme,
        persologDefault,
        persologHighContrast,
    };
}
